import { IPromise } from 'angular';
import Fuse from 'fuse.js';
import { IFontFamilyModel, IFontStyleModel, IFoundryModel } from '..';
import {
    isIFontFamilyModel,
    isIFontStyle,
    IVFInfo,
    REGISTERED_AXES,
} from '../classes';
import { arraysIntersect } from './arrayIntersect';
import { hasOwnProperty } from './hasOwnProperty';

/**
 *  @file helper functions for filtering with FUSE (fuzzy search engine)
 *
 * TODO: the filter fuzzy search fuctionality should be refactored into classes that compose
 * or extend fuse for each Type we need to search.
 *
 * TOOD: the entire filter setup needs documentation, and probably some big picture refactoring to perform better.
 */

const FUZE_OPTIONS_BASE = {
    tokenize: true,
    matchAllTokens: true,
    caseSensitive: false,
    shouldSort: false,
    threshold: 0.1,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    minMatchCharLength: 5,
};

const FUSE_OPTIONS_HAS_LATINIZED_NAME = Object.assign({}, FUZE_OPTIONS_BASE, {
    keys: ['latin_name'],
});

const FUSE_OPTIONS_NO_NAME = Object.assign({}, FUZE_OPTIONS_BASE, {
    keys: ['name'],
});

export type FilteredModels = IFoundryModel | IFontFamilyModel | IFontStyleModel;

// tn is synonumous with OS/2 sizes [100, 200, 300, 400, ... ,900]
// parametric is 0-1000 in steps of 10
export type SizeProp = 'tn_width' | 'tn_weight';
// an object property name to which the filter should be applied.
export type EntryProp = string;
// export type Filters = Record<EntryProp, FilterMatch>
export type Entry = Record<EntryProp, unknown>;

export const VARIABLE_FILTER_OPTIONS = [
    ...REGISTERED_AXES,
    'custom',
    'static',
] as const;
export type VariableFilterOptions = (typeof VARIABLE_FILTER_OPTIONS)[number];

export class Filters {
    id?: number | number[] = [];
    name?: string | string[] = [];
    family?: number[] = [];
    language?: number[] = [];
    posture?: number[] = [];
    designer?: number[] = [];
    foundry?: number[] = [];
    recommended_size?: number[] = [];
    recommended_function?: number[] = [];
    slug?: string[] = [];
    // size props
    // this is an array, but only every contains 1..
    width_range?: number[] = [];
    weight_range?: number[] = [];
    date?: {
        startDate?: Date;
        start: { month?: number; year?: number };
        endDate?: Date;
        end: { month?: number; year?: number };
    };
    variable?: VariableFilterOptions[];
}

// currently only used in the foundry service.  TODO: move filtering into the foundry service.
export function applyFilters<T extends FilteredModels>(
    promise: IPromise<T[]>,
    filters: Filters
): IPromise<T[]> {
    return promise.then((entries) => applyFiltersSync<T>(entries, filters));
}

// use by applyFilters and tn.fonts.api.service.ts
// Apply multiple filters to a collection.
export function applyFiltersSync<T extends FilteredModels = IFontFamilyModel>(
    entries: T[],
    filters: Filters
): T[] {
    function _reducer(prevEntries: T[], filterKey: string) {
        // short circuit do anything if we've already reduced the collection to nothing.
        if (prevEntries.length === 0) return [];
        if (filterKey == 'name') {
            return applyNameFilterSync(prevEntries, filters.name);
        }
        if (filterKey == 'weight_range' && filters.weight_range) {
            return applyRangeFilterSync<T>(
                prevEntries,
                'tn_weight',
                filters.weight_range
            );
        }
        if (filterKey == 'width_range' && filters.width_range) {
            return applyRangeFilterSync<T>(
                prevEntries,
                'tn_width',
                filters.width_range
            );
        }
        if (filterKey == 'language' && filters.language && !filters.id) {
            return applyFilterSync<T, number>(
                prevEntries.filter((item) => item && item.language),
                filterKey,
                filters.language
            );
        }

        if (filterKey == 'variable' && filters.variable) {
            return applyVariableAxesFilter(prevEntries, filters.variable);
        }
        // apply generic filtering if one of our type specific filters didn't get called.
        return applyFilterSync<T>(prevEntries, filterKey, filters[filterKey]);
    }

    const filterKeys = Object.keys(filters);
    const filterResults = filterKeys.reduce(_reducer, entries);
    return filterResults;
}

export function applyNameFilterSync<
    T extends FilteredModels = IFontFamilyModel
>(entries: T[], names: string | string[] = ''): T[] {
    // we can't sniff out if the items have a cached latinized name here
    if (entries.length <= 0) return [];
    // in case of an array use the fuse | operator
    // see: https://fusejs.io/examples.html#extended-search
    const name = Array.isArray(names)
        ? names.map((s) => s.trim()).join('|')
        : names.trim();
    // no filtering necessary.
    if (!name) return entries;
    const opts = entries[0].latin_name
        ? FUSE_OPTIONS_HAS_LATINIZED_NAME
        : FUSE_OPTIONS_NO_NAME;
    const fuse = new Fuse(entries, opts);
    return fuse.search(name);
}

/**
 * @param value a numeric value from FontWidth, FontWeight, FontOpticalSize, FontPosture
 * @returns
 */
export function isPropVariable(value: number): boolean {
    return value == 0 || value == undefined || value == null;
}

export function isPropInRange(
    value: number,
    min: number,
    max: number
): boolean {
    return value >= min && value <= max;
}

export function isVariablePropInRange(
    size: number,
    min: number,
    max: number
): boolean {
    return isPropVariable(size) || isPropInRange(size, min, max);
}

export function applyRangeFilterSync<
    T extends FilteredModels = IFontFamilyModel
>(entries: T[], prop: 'tn_width' | 'tn_weight', range: number[]): T[] {
    // this is meant to filter fonts by size.
    if (entries.length <= 0) return [];

    // Select filtered property, default to tn (OS/2)
    // Copy range and insert lower bound if only one value present
    const [min, max] =
        range.length === 1 ? [0, range[0]] : [range[0], range[1]];
    return entries.filter((entry) => {
        const value = entry[prop];
        // if the size array on the font is an array, then check that all of its sizes are within our filter range.
        if (Array.isArray(value) && isIFontFamilyModel(entry)) {
            // it looks like variable families end up with an empty array
            // I'm assuming aggregateFields filters out undefineds.
            if (entry.variable && value.length == 0) {
                return true;
            }
            return value.some((size) => isVariablePropInRange(size, min, max));
        }
        return isVariablePropInRange(value, min, max);
    });
}

function variableAxesFilterMatch(
    info?: IVFInfo,
    options?: VariableFilterOptions[]
) {
    if (!options || options.length == 0) {
        return true;
    }
    return options.some((option) => {
        if (!info || !info.axes || !info.order) return false;
        if (option == 'custom') {
            return info.order.some((axis) => !REGISTERED_AXES.includes(axis));
        }
        return !!info.axes[option];
    });
}

export function applyVariableAxesFilter<
    T extends FilteredModels = IFontFamilyModel
>(entries: T[], options: VariableFilterOptions[]): T[] {
    // console.log('applyVariableAxesFilter', entries, options);
    if (entries.length <= 0) return [];
    if (options.length == 0) return entries;
    const includeStatic = options.includes('static');
    return entries.filter((entry) => {
        if (isIFontStyle(entry)) {
            if (includeStatic && !entry.variable) return true;
            return variableAxesFilterMatch(entry.variable_info, options);
        }
        if (isIFontFamilyModel(entry)) {
            if (includeStatic && !entry.variable) return true;
            return entry.variable_info.some((info) =>
                variableAxesFilterMatch(info, options)
            );
        }
    });
}

/**
 * Apply a set of filters to an array of entries.
 *
 * @param entries
 * @param prop
 * @param filterMatch
 * @returns
 */
export function applyFilterSync<T, F = string>(
    entries: T[],
    prop: EntryProp,
    filterMatch: F | F[]
): T[] {
    //only filter if entries have filterKey as a filed
    //only check one as all entries will be the same format
    if (entries[0] && !hasOwnProperty(entries[0], prop)) {
        return entries;
    }
    // cast filterValue to array
    const needle: Array<F> = Array.isArray(filterMatch)
        ? filterMatch
        : [filterMatch];
    // empty arrays are a no-op
    if (needle.length == 0) return entries;
    return entries.filter((entry: T): boolean => {
        const value = entry[prop];
        // if the entry property is an array, then check for any intersection.
        if (Array.isArray(value)) {
            return arraysIntersect(value, needle).length > 0;
        }
        // otherwise the entry property is scalar and _filterMatch is an array,
        // so we can just check for inclusion.
        return needle.includes(value);
    });
}
